import React, { useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, TextInput, Switch, StyleSheet, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons as Icon } from '@expo/vector-icons';
import { Colors } from '../constants/colors';
import { useAuth } from '../context/AuthContext';

function SettingsPanel({ title, onClose, children }) {
  return (
    <View style={panelStyles.overlay}>
      <SafeAreaView style={panelStyles.safe}>
        <View style={panelStyles.header}>
          <TouchableOpacity onPress={onClose}><Icon name="chevron-back" size={24} color={Colors.primary} /></TouchableOpacity>
          <Text style={panelStyles.title}>{title}</Text>
          <View style={{ width: 24 }} />
        </View>
        <ScrollView contentContainerStyle={panelStyles.content}>{children}</ScrollView>
      </SafeAreaView>
    </View>
  );
}
var panelStyles = StyleSheet.create({
  overlay: { ...StyleSheet.absoluteFillObject, backgroundColor: Colors.bgTop, zIndex: 10 },
  safe: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12 },
  title: { fontSize: 20, fontWeight: '700', color: Colors.textDark },
  content: { padding: 20, paddingBottom: 40 },
});

export default function SettingsScreen() {
  var syncState = useState(false);
  var sync = syncState[0];
  var setSync = syncState[1];
  var panelState = useState(null);
  var panel = panelState[0];
  var setPanel = panelState[1];
  var { user, logout } = useAuth();

  function handleLogout() {
    Alert.alert('Log Out', 'Are you sure?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Log Out', style: 'destructive', onPress: logout },
    ]);
  }

  function renderPanel() {
    if (panel === 'edit') {
      return (
        <SettingsPanel title="Edit Profile" onClose={function() { setPanel(null); }}>
          <View style={styles.profilePhotoWrap}>
            <View style={styles.profilePhoto}><Icon name="person" size={40} color={Colors.textMid} /></View>
            <TouchableOpacity><Text style={styles.changePhotoText}>Change photo</Text></TouchableOpacity>
          </View>
          {[['Display name', user ? user.name : 'Jackie Crowley'], ['Username', 'jackie_c_recovery'], ['Email', user ? user.email : 'jackie@example.com'], ['Phone', '+1 (604) 555-0123']].map(function(f, i) {
            return (
              <View key={i} style={styles.formField}>
                <Text style={styles.formLabel}>{f[0]}</Text>
                <TextInput defaultValue={f[1]} style={styles.formInput} />
              </View>
            );
          })}
          <TouchableOpacity style={styles.saveButton}><Text style={styles.saveButtonText}>Save Changes</Text></TouchableOpacity>
        </SettingsPanel>
      );
    }
    if (panel === 'password') {
      return (
        <SettingsPanel title="Change Password" onClose={function() { setPanel(null); }}>
          {['Current password', 'New password', 'Confirm new password'].map(function(l, i) {
            return (
              <View key={i} style={styles.formField}>
                <Text style={styles.formLabel}>{l}</Text>
                <TextInput placeholder={l} secureTextEntry style={styles.formInput} />
              </View>
            );
          })}
          <TouchableOpacity style={styles.saveButton}><Text style={styles.saveButtonText}>Update Password</Text></TouchableOpacity>
        </SettingsPanel>
      );
    }
    if (panel === 'sync') {
      return (
        <SettingsPanel title="Advanced Sync Options" onClose={function() { setPanel(null); }}>
          {[["Sync today's data", 'Share completion with clinician'], ['Sync custom range', 'Choose a date range to sync'], ['Auto-sync all data', 'Automatically sync everything']].map(function(item, i) {
            return (
              <View key={i} style={styles.syncItem}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.syncLabel}>{item[0]}</Text>
                  <Text style={styles.syncDesc}>{item[1]}</Text>
                </View>
                <Switch trackColor={{ true: Colors.primary }} />
              </View>
            );
          })}
        </SettingsPanel>
      );
    }
    if (panel === 'theme') {
      var themes = [['Calm', '🌅', true], ['Playful', '🐻', false], ['Simple (light)', '✨', false], ['Simple (dark)', '🌙', false]];
      return (
        <SettingsPanel title="Change Theme" onClose={function() { setPanel(null); }}>
          <View style={styles.themeGrid}>
            {themes.map(function(t, i) {
              return (
                <TouchableOpacity key={i} style={[styles.themeCard, t[2] && styles.themeCardActive]}>
                  <View style={styles.themePreview}><Text style={{ fontSize: 30 }}>{t[1]}</Text></View>
                  <Text style={styles.themeName}>{t[0]}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </SettingsPanel>
      );
    }
    if (panel === 'about' || panel === 'privacy' || panel === 'terms') {
      var info = {
        about: { title: 'About us', body: 'Momentum is a gamified compliance app developed in partnership with Vancouver Coastal Health and Azar Lab. Our mission is to support patients on their recovery journey through daily quests, community connection, and meaningful rewards.\n\nVersion 1.0 · Build 2025.01' },
        privacy: { title: 'Privacy policy', body: 'Your privacy is our priority. You choose how your data is shared: automatic sync, manual sharing during clinic visits, or no sharing. All data is encrypted at rest and in transit. We never sell your personal information.\n\nYou can modify your data-sharing preferences at any time from Settings.' },
        terms: { title: 'Terms and conditions', body: 'By using Momentum, you agree to use the app for its intended purpose of supporting treatment compliance. All community posts must follow our guidelines: be supportive, non-triggering, and non-identifying.\n\nRewards are subject to clinician verification and may vary based on program availability.' },
      };
      var item = info[panel];
      return (
        <SettingsPanel title={item.title} onClose={function() { setPanel(null); }}>
          <View style={styles.infoCard}>
            <Text style={styles.infoTitle}>{item.title}</Text>
            <Text style={styles.infoBody}>{item.body}</Text>
          </View>
        </SettingsPanel>
      );
    }
    return null;
  }

  var menuGroups = [
    ['Account Settings', [['Edit profile', 'edit', 'create-outline'], ['Change password', 'password', 'lock-closed-outline'], ['Advanced Sync options', 'sync', 'sync-outline'], ['Sync data', 'toggle', 'cloud-upload-outline'], ['Change theme', 'theme', 'color-palette-outline']]],
    ['More', [['About us', 'about', 'information-circle-outline'], ['Privacy policy', 'privacy', 'shield-checkmark-outline'], ['Terms and conditions', 'terms', 'document-text-outline']]],
  ];

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safe} edges={['top']}>
        <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          <View style={styles.profileHeader}>
            <View style={styles.profileAvatar}><Icon name="person" size={28} color={Colors.textMid} /></View>
            <Text style={styles.profileName}>{user ? user.name : 'Jackie Crowley'}</Text>
          </View>

          {menuGroups.map(function(group, gi) {
            return (
              <View key={gi} style={styles.menuGroup}>
                <Text style={styles.menuGroupTitle}>{group[0]}</Text>
                <View style={styles.menuCard}>
                  {group[1].map(function(item, ii) {
                    var isToggle = item[1] === 'toggle';
                    var isLast = ii === group[1].length - 1;
                    return (
                      <TouchableOpacity
                        key={ii}
                        style={[styles.menuItem, !isLast && styles.menuItemBorder]}
                        onPress={function() { if (!isToggle) setPanel(item[1]); }}
                        activeOpacity={isToggle ? 1 : 0.7}
                      >
                        <Text style={styles.menuLabel}>{item[0]}</Text>
                        {isToggle ? (
                          <Switch value={sync} onValueChange={setSync} trackColor={{ true: Colors.primary }} />
                        ) : (
                          <Icon name="chevron-forward" size={16} color={Colors.textLight} />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            );
          })}

          <TouchableOpacity style={styles.logoutBtn} onPress={handleLogout}>
            <Text style={styles.logoutText}>Log Out</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
      {renderPanel()}
    </View>
  );
}

var styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bgTop },
  safe: { flex: 1 },
  scrollContent: { paddingBottom: 100 },
  profileHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 20, paddingTop: 16 },
  profileAvatar: { width: 52, height: 52, borderRadius: 26, backgroundColor: '#C0D5CC', alignItems: 'center', justifyContent: 'center' },
  profileName: { fontSize: 20, fontWeight: '700', color: Colors.textDark },
  menuGroup: { paddingHorizontal: 20, marginBottom: 16 },
  menuGroupTitle: { fontSize: 12, color: Colors.textLight, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  menuCard: { backgroundColor: Colors.card, borderRadius: 12, overflow: 'hidden' },
  menuItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 14, paddingHorizontal: 16 },
  menuItemBorder: { borderBottomWidth: 1, borderBottomColor: Colors.border },
  menuLabel: { fontSize: 15, color: Colors.textDark },
  logoutBtn: { marginHorizontal: 20, marginTop: 10, paddingVertical: 14, borderRadius: 14, backgroundColor: 'rgba(224,85,85,0.1)', alignItems: 'center' },
  logoutText: { fontSize: 15, fontWeight: '600', color: Colors.danger },
  // Panel sub-views
  profilePhotoWrap: { alignItems: 'center', marginBottom: 24 },
  profilePhoto: { width: 80, height: 80, borderRadius: 40, backgroundColor: '#C0D5CC', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  changePhotoText: { fontSize: 14, color: Colors.primary, fontWeight: '600' },
  formField: { marginBottom: 16 },
  formLabel: { fontSize: 12, fontWeight: '600', color: Colors.textMid, marginBottom: 4 },
  formInput: { backgroundColor: '#fff', borderWidth: 1, borderColor: Colors.border, borderRadius: 10, padding: 12, fontSize: 15 },
  saveButton: { backgroundColor: Colors.primary, paddingVertical: 14, borderRadius: 14, alignItems: 'center', marginTop: 8 },
  saveButtonText: { color: '#fff', fontSize: 15, fontWeight: '600' },
  syncItem: { flexDirection: 'row', alignItems: 'center', backgroundColor: Colors.card, borderRadius: 14, padding: 16, marginBottom: 8 },
  syncLabel: { fontSize: 15, fontWeight: '600', color: Colors.textDark },
  syncDesc: { fontSize: 12, color: Colors.textLight, marginTop: 3 },
  themeGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  themeCard: { width: '47%', borderRadius: 16, overflow: 'hidden', borderWidth: 3, borderColor: 'transparent' },
  themeCardActive: { borderColor: Colors.primary },
  themePreview: { height: 90, backgroundColor: '#D8EAE0', alignItems: 'center', justifyContent: 'center' },
  themeName: { textAlign: 'center', paddingVertical: 10, fontSize: 13, fontWeight: '600', color: Colors.textDark, backgroundColor: Colors.card },
  infoCard: { backgroundColor: Colors.card, borderRadius: 14, padding: 18 },
  infoTitle: { fontSize: 18, fontWeight: '700', color: Colors.textDark, marginBottom: 8 },
  infoBody: { fontSize: 14, color: Colors.textMid, lineHeight: 22 },
});
